import React, { useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATE_KEY = 'km-chad-proposals';

// AC-4 — a write AI Chad proposes is never executed until the person presses
// Confirm. The card's final state outlives a reload: the proposal itself is
// stored on the message, its answer is remembered here.
const readStates = () => {
  try { return JSON.parse(localStorage.getItem(STATE_KEY) || '{}'); } catch (e) { return {}; }
};

export const proposalState = (id) => readStates()[id] || null;

const writeState = (id, state) => {
  try {
    const all = readStates();
    all[id] = state;
    localStorage.setItem(STATE_KEY, JSON.stringify(all));
  } catch (e) { /* a full store must not break the card */ }
};

export const ActionCard = ({ proposal, onDone }) => {
  const [state, setState] = useState(() => proposalState(proposal.id) || { phase: 'open' });
  const [busy, setBusy] = useState(false);

  const confirm = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/ai-chad/action`, {
        action: proposal.action,
        project_id: proposal.project_id || null,
        payload: proposal.payload || {},
        proposal_id: proposal.id,
      });
      const next = { phase: 'done', note: `Done · ${proposal.summary}` };
      setState(next);
      writeState(proposal.id, next);
      if (onDone) {
        onDone({
          action: proposal.action,
          success: true,
          message: res?.data?.message || '',
          project_id: proposal.project_id || null,
          result: res?.data || null,
        });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (detail?.message || 'that did not go through');
      const next = { phase: 'failed', note: `Not done · ${message}` };
      setState(next);
      writeState(proposal.id, next);
      if (onDone) {
        onDone({
          action: proposal.action,
          success: false,
          message,
          project_id: proposal.project_id || null,
          result: null,
        });
      }
    } finally {
      setBusy(false);
    }
  }, [busy, proposal, onDone]);

  const cancel = useCallback(() => {
    const next = { phase: 'cancelled', note: 'Cancelled' };
    setState(next);
    writeState(proposal.id, next);
  }, [proposal.id]);

  if (state.phase !== 'open') {
    return (
      <div className="km-chad__act" data-testid="chad-action-card">
        <div className="km-chad__act-sum" data-testid="chad-action-state">{state.note}</div>
      </div>
    );
  }

  return (
    <div className="km-chad__act" data-testid="chad-action-card">
      <div className="km-chad__act-sum">{proposal.summary}</div>
      <div className="km-chad__act-row">
        <button type="button" className="km-chad__btn" onClick={confirm} disabled={busy} data-testid="chad-action-confirm">
          {busy ? 'Working…' : 'Confirm'}
        </button>
        <button type="button" className="km-chad__btn km-chad__btn--quiet" onClick={cancel} disabled={busy} data-testid="chad-action-cancel">
          Cancel
        </button>
      </div>
    </div>
  );
};

export default ActionCard;
